import { poe2 } from '../../game';
import { cantHaveSocketsItemClasses, itemTypesWithTwoSockets } from '../data/poe2/item-lists';
import { Rarity } from '../models/enums';
import { GameItem } from '../models/game-item.models';

export type GearQualityPrefix = 'Superior' | 'Exceptional';

export function hasExtraSocket(item: GameItem): boolean {
  if (!poe2 || item.sockets === undefined || item.sockets <= 0) return false;
  if (cantHaveSocketsItemClasses.includes(item.itemClass)) return false;

  const maxNormalSockets = itemTypesWithTwoSockets.includes(item.itemClass) ? 2 : 1;
  return item.sockets > maxNormalSockets;
}

export function getGearQualityPrefix(item: GameItem): GearQualityPrefix | null {
  if (poe2) {
    if (item.quality !== undefined && item.quality >= 21) return 'Exceptional';
    if (hasExtraSocket(item)) return 'Exceptional';
    if (item.quality !== undefined && item.quality >= 1) return 'Superior';
    return null;
  }

  if (item.quality !== undefined && item.quality >= 1) return 'Superior';
  return null;
}

function hasQualityPrefix(name: string): boolean {
  return name.startsWith('Superior ') || name.startsWith('Exceptional ');
}

export function getGameItemExampleName(item: GameItem): string {
  const isRareOrIdentifiedUnique =
    item.rarity === Rarity.Rare || (item.rarity === Rarity.Unique && item.identified !== false);

  if (isRareOrIdentifiedUnique) {
    return item.baseType;
  }

  if (item.rarity !== Rarity.Normal) {
    return item.name ?? item.baseType;
  }

  if (item.name && hasQualityPrefix(item.name)) {
    return item.name;
  }

  const prefix = getGearQualityPrefix(item);
  if (prefix) {
    return `${prefix} ${item.baseType}`;
  }

  return item.name ?? item.baseType;
}

// Label-viewer socket format (see socket-condition.util.ts for the full syntax model):
// game clipboard text, or L-prefixed colours when a single group is fully linked.
// Concrete items map X → W (wildcard is UI/filter only).
export function getGameItemSocketsDisplay(item: GameItem): string | undefined {
  if (item.socketsDisplay) return item.socketsDisplay;

  if (item.socketGroup) {
    const colours = concreteItemSocketColours(item.socketGroup);
    const totalSockets = item.sockets ?? colours.length;
    return linkedGroupToLabelDisplay(colours, totalSockets);
  }

  if (item.linkedSockets && item.linkedSockets > 0) {
    const linkedColours = 'W'.repeat(item.linkedSockets);
    const totalSockets = item.sockets ?? item.linkedSockets;
    return linkedGroupToLabelDisplay(linkedColours, totalSockets);
  }

  if (item.sockets && item.sockets > 0) {
    return 'W'.repeat(item.sockets);
  }

  return undefined;
}

// Clipboard form for item text export / paste round-trip (PoE2 uses S instead of W).
export function getGameItemSocketsClipboard(item: GameItem, spiritSockets = false): string | undefined {
  const display = getGameItemSocketsDisplay(item);
  if (!display) return undefined;
  return labelSocketsToClipboard(display, spiritSockets);
}

function concreteItemSocketColours(colours: string): string {
  return colours
    .toUpperCase()
    .split('')
    .map(char => (char === 'X' ? 'W' : char))
    .join('');
}

function linkedGroupToLabelDisplay(linkedColours: string, totalSockets: number): string | undefined {
  if (!linkedColours) return undefined;

  const leftover = Math.max(0, totalSockets - linkedColours.length);
  if (leftover > 0) {
    const linkedPart = linkedColours.split('').join('-');
    const unlinkedPart = Array(leftover).fill('W').join(' ');
    return `${linkedPart} ${unlinkedPart}`;
  }

  return linkedColours.length > 1 ? 'L' + linkedColours : linkedColours;
}

function labelSocketsToClipboard(display: string, spiritSockets: boolean): string {
  const mapLetter = (char: string): string => {
    const upper = char.toUpperCase();
    if (spiritSockets && (upper === 'W' || upper === 'X' || upper === 'S')) return 'S';
    return upper;
  };

  if (display.startsWith('L')) {
    return display.slice(1).split('').map(mapLetter).join('-');
  }

  if (/[-\s]/.test(display)) {
    return display.replace(/[RGBWXS]/gi, match => mapLetter(match));
  }

  return display.split('').map(mapLetter).join(' ');
}
